import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Link,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Person as PersonIcon,
  Lock as LockIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import AuthService from '../services/AuthService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8082/api';
const DEFAULT_PRICING_PLAN_URL = 'https://foysonis.com/pricing';

// Generate a random math challenge
const generateMathChallenge = () => {
  const num1 = Math.floor(Math.random() * 10) + 1; // 1-10
  const num2 = Math.floor(Math.random() * 10) + 1; // 1-10
  const operators = ['+', '-'];
  const operator = operators[Math.floor(Math.random() * operators.length)];

  let answer;
  if (operator === '+') {
    answer = num1 + num2;
  } else {
    // Ensure positive result for subtraction
    const [a, b] = num1 >= num2 ? [num1, num2] : [num2, num1];
    answer = a - b;
    return { question: `${a} ${operator} ${b}`, answer };
  }

  return { question: `${num1} ${operator} ${num2}`, answer };
};

// Small "label + (?)" combo for a field whose purpose isn't obvious from the
// label alone -- Tenant Code / Warehouse Code, both new fields most signups
// have never had to think about before.
const LabelWithInfo = ({ label, tooltip }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
    {label}
    <Tooltip title={tooltip} arrow placement="top">
      <InfoOutlinedIcon sx={{ fontSize: 15, opacity: 0.6, cursor: 'help' }} />
    </Tooltip>
  </Box>
);

const RegisterPage = ({ colors, onNavigateToLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  // Company/tenant code/warehouse code -- company name drives the SUGGESTED
  // (editable) defaults for the other two, replacing the old silent
  // "firstName lastName's Company" fallback per explicit product direction.
  // codesTouched tracks whether the user has hand-edited a code field, so a
  // later companyName edit doesn't clobber their intentional override.
  const [companyName, setCompanyName] = useState('');

  // Bot prevention state
  const [honeypot, setHoneypot] = useState(''); // Hidden field - bots fill this
  const [formLoadTime] = useState(Date.now()); // Track when form loaded
  const [mathChallenge, setMathChallenge] = useState(() => generateMathChallenge());
  const [mathAnswer, setMathAnswer] = useState('');

  // "Choose a plan" link -- a SYSTEM_CONFIG lookup value (PRICING_PLAN_URL) so it
  // can be updated later without a redeploy. Falls back to the hardcoded default
  // if the lookup is missing or the fetch fails (public endpoint, no auth needed).
  const [pricingPlanUrl, setPricingPlanUrl] = useState(DEFAULT_PRICING_PLAN_URL);

  // Regenerate math challenge on mount
  useEffect(() => {
    setMathChallenge(generateMathChallenge());
  }, []);

  useEffect(() => {
    fetch(`${API_BASE_URL}/v1/lookup/category/SYSTEM_CONFIG/code/PRICING_PLAN_URL`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.displayName) {
          setPricingPlanUrl(data.displayName);
        }
      })
      .catch(() => {
        // Keep the hardcoded default
      });
  }, []);

  const validateForm = () => {
    // Bot prevention checks
    // 1. Honeypot check - should be empty (bots fill hidden fields). Real bug found
    // live 2026-10-01: a legitimate user got "Registration failed. Please try
    // again." on their first attempt, with no backend-side registration failure at
    // all (the pending email_verification_tokens row existed, meaning the honeypot
    // alone blocked a real submission before it ever reached the server) -- most
    // likely an aggressive browser/password-manager autofill populating this
    // absolutely-positioned-offscreen field despite autoComplete="off" and no name
    // attribute. .trim() tolerates a stray space/newline some autofill behaviors
    // insert into an empty field without weakening the real bot check (an actual
    // bot filling this field writes real content, not just whitespace).
    if (honeypot.trim()) {
      console.log('Bot detected: honeypot filled');
      setError('Registration failed. Please try again.');
      return false;
    }

    // 2. Time-based check - form should take at least 3 seconds to fill
    const timeElapsed = Date.now() - formLoadTime;
    if (timeElapsed < 3000) {
      console.log('Bot detected: form submitted too quickly');
      setError('Please take your time filling out the form.');
      return false;
    }

    // 3. Math challenge check
    if (!mathAnswer.trim()) {
      setError('Please solve the math problem');
      return false;
    }
    if (parseInt(mathAnswer.trim(), 10) !== mathChallenge.answer) {
      setError('Incorrect answer to the math problem. Please try again.');
      setMathChallenge(generateMathChallenge()); // Generate new challenge
      setMathAnswer('');
      return false;
    }

    // Standard validation
    if (!firstName.trim()) {
      setError('First name is required');
      return false;
    }
    if (!lastName.trim()) {
      setError('Last name is required');
      return false;
    }
    if (!email.trim()) {
      setError('Email is required');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError('Please enter a valid email address');
      return false;
    }
    if (!password) {
      setError('Password is required');
      return false;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    }
    return true;
  };

  // "Create Account" no longer submits directly -- it first shows the typed
  // email back for a confirm/edit step, per explicit product direction
  // 2026-10-01: a typo'd email means the user never receives the
  // verification link and the signup silently goes nowhere (and a slightly
  // wrong domain might not even get caught by the new server-side MX check,
  // e.g. a typo that still lands on a real, different domain). Actual
  // submission moved to confirmRegistration(), called from the dialog.
  const [emailConfirmOpen, setEmailConfirmOpen] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setEmailConfirmOpen(true);
  };

  const confirmRegistration = async () => {
    setEmailConfirmOpen(false);
    setIsSubmitting(true);
    try {
      await AuthService.register(email.trim(), password, firstName.trim(), lastName.trim(), {
        companyName: companyName.trim() || undefined,
      });
      setRegistrationSuccess(true);
    } catch (err) {
      setError(err.message || 'Registration failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  if (registrationSuccess) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: colors.background,
          p: 2,
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 400,
            bgcolor: colors.surface,
            border: `1px solid ${colors.border}`,
            p: 4,
            textAlign: 'center',
          }}
        >
          <EmailIcon sx={{ fontSize: 64, color: '#22c55e', mb: 2 }} />
          <Typography
            variant="h5"
            sx={{
              color: colors.textPrimary,
              fontWeight: 600,
              mb: 2,
            }}
          >
            Check Your Email
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: colors.textSecondary, mb: 2 }}
          >
            We've sent a verification link to your email address.
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: colors.textSecondary, mb: 3 }}
          >
            Please click the link in the email to verify your account before logging in.
          </Typography>
          <Button
            fullWidth
            variant="contained"
            onClick={onNavigateToLogin}
            sx={{
              bgcolor: '#22c55e',
              color: colors.background,
              py: 1.5,
              fontWeight: 600,
              letterSpacing: '0.1em',
              '&:hover': {
                bgcolor: '#16a34a',
              },
            }}
          >
            Go to Login
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: colors.background,
        p: 2,
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 520,
          bgcolor: colors.surface,
          border: `1px solid ${colors.border}`,
          p: 4,
        }}
      >
        {/* Logo / Brand */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography
            variant="h4"
            sx={{
              color: '#22c55e',
              fontWeight: 700,
              letterSpacing: '-0.025em',
              mb: 1,
            }}
          >
            Foysonis SCM
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: colors.textSecondary }}
          >
            Create your account
          </Typography>
        </Box>

        {/* Free trial blurb */}
        <Box
          sx={{
            mb: 3,
            p: 2,
            bgcolor: 'rgba(34, 197, 94, 0.08)',
            borderLeft: '4px solid #22c55e',
          }}
        >
          <Typography variant="body2" sx={{ color: colors.textSecondary }}>
            You're signing up for a <strong>free trial</strong> of our <strong>STARTER</strong> plan &mdash; we won't
            ask for any payment information to let you try out the system.{' '}
            <Link
              href={pricingPlanUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: '#22c55e',
                fontWeight: 600,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Go here
            </Link>{' '}
            to see what's included with the STARTER plan.
            <br />
            <br />
            After you submit this form, we'll send a confirmation link to the email address you provide below.
            You'll need to click that link to verify your email before you can log in.
          </Typography>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 0,
              bgcolor: 'rgba(239, 68, 68, 0.1)',
              borderLeft: '4px solid #ef4444',
              '& .MuiAlert-icon': {
                color: '#ef4444',
              },
              '& .MuiAlert-message': {
                overflowWrap: 'break-word',
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
              },
            }}
          >
            {error}
          </Alert>
        )}

        {/* Registration Form */}
        <Box component="form" onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              label="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isSubmitting}
              autoFocus
              autoComplete="given-name"
              inputProps={{ maxLength: 100 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon sx={{ color: colors.textMuted }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: colors.background,
                  '& fieldset': {
                    borderColor: colors.borderLight,
                  },
                  '&:hover fieldset': {
                    borderColor: '#22c55e',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#22c55e',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: colors.textSecondary,
                },
                '& .MuiInputBase-input': {
                  color: colors.textPrimary,
                },
              }}
            />

            <TextField
              fullWidth
              label="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isSubmitting}
              autoComplete="family-name"
              inputProps={{ maxLength: 100 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: colors.background,
                  '& fieldset': {
                    borderColor: colors.borderLight,
                  },
                  '&:hover fieldset': {
                    borderColor: '#22c55e',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#22c55e',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: colors.textSecondary,
                },
                '& .MuiInputBase-input': {
                  color: colors.textPrimary,
                },
              }}
            />
          </Box>

          <TextField
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isSubmitting}
            autoComplete="email"
            inputProps={{ maxLength: 254 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon sx={{ color: colors.textMuted }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                bgcolor: colors.background,
                '& fieldset': {
                  borderColor: colors.borderLight,
                },
                '&:hover fieldset': {
                  borderColor: '#22c55e',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#22c55e',
                },
              },
              '& .MuiInputLabel-root': {
                color: colors.textSecondary,
              },
              '& .MuiInputBase-input': {
                color: colors.textPrimary,
              },
            }}
          />

          <TextField
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isSubmitting}
            autoComplete="new-password"
            inputProps={{ maxLength: 128 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ color: colors.textMuted }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    sx={{ color: colors.textMuted }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                bgcolor: colors.background,
                '& fieldset': {
                  borderColor: colors.borderLight,
                },
                '&:hover fieldset': {
                  borderColor: '#22c55e',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#22c55e',
                },
              },
              '& .MuiInputLabel-root': {
                color: colors.textSecondary,
              },
              '& .MuiInputBase-input': {
                color: colors.textPrimary,
              },
            }}
          />

          <TextField
            fullWidth
            label={(
              <LabelWithInfo
                label="Company Name"
                tooltip="Your company's name as customers and carriers will see it -- appears on packing slips, labels, and other documents generated by the system. Also used as the default name shown throughout the platform."
              />
            )}
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isSubmitting}
            autoComplete="organization"
            inputProps={{ maxLength: 255 }}
            helperText="Optional -- leave blank and we'll use your name to name the account"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <BusinessIcon sx={{ color: colors.textMuted }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                bgcolor: colors.background,
                '& fieldset': { borderColor: colors.borderLight },
                '&:hover fieldset': { borderColor: '#22c55e' },
                '&.Mui-focused fieldset': { borderColor: '#22c55e' },
              },
              '& .MuiInputLabel-root': { color: colors.textSecondary },
              '& .MuiInputBase-input': { color: colors.textPrimary },
              '& .MuiFormHelperText-root': { color: colors.textMuted },
            }}
          />

          {/* Tenant Code / Warehouse Code removed from signup 2026-10-01 per
              explicit product direction: these are consequential, rarely-
              thought-about-at-signup identifiers (they show up in API
              headers, support tickets, integrations), so the backend now
              always auto-generates both (resolveTenantCode/resolveSystemCode
              with blank input) and the tenant's own admin can set them
              deliberately afterward, in Account Settings -- see
              AtomApp.js's accountForm tenantCode/warehouseCode fields. */}

          {/* Honeypot field - hidden from users, bots will fill it. name/autoComplete
              deliberately avoid anything resembling a real field (email/phone/address/
              website) that a browser or password manager's autofill heuristics might
              otherwise latch onto -- a real false positive was caught live 2026-10-01
              (see the honeypot.trim() comment above). display: 'none' (rather than
              only off-screen positioning) is also less likely to be included in an
              autofill pass than a field that's merely moved off-screen but still
              laid out and visible to the DOM/accessibility tree. */}
          <TextField
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
            name="hp_do_not_fill_9f3a"
            autoComplete="off"
            tabIndex={-1}
            sx={{
              display: 'none',
            }}
            aria-hidden="true"
          />

          {/* Math challenge - bot prevention */}
          <TextField
            fullWidth
            label={`What is ${mathChallenge.question}?`}
            type="text"
            inputMode="numeric"
            value={mathAnswer}
            onChange={(e) => setMathAnswer(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isSubmitting}
            autoComplete="off"
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                bgcolor: colors.background,
                '& fieldset': {
                  borderColor: colors.borderLight,
                },
                '&:hover fieldset': {
                  borderColor: '#22c55e',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#22c55e',
                },
              },
              '& .MuiInputLabel-root': {
                color: colors.textSecondary,
              },
              '& .MuiInputBase-input': {
                color: colors.textPrimary,
              },
            }}
          />

          <Button
            fullWidth
            variant="contained"
            type="submit"
            disabled={isSubmitting}
            sx={{
              bgcolor: '#22c55e',
              color: colors.background,
              py: 1.5,
              fontWeight: 600,
              letterSpacing: '0.1em',
              '&:hover': {
                bgcolor: '#16a34a',
              },
              '&.Mui-disabled': {
                bgcolor: colors.surfaceElevated,
                color: colors.textMuted,
              },
            }}
          >
            {isSubmitting ? (
              <CircularProgress size={24} sx={{ color: colors.background }} />
            ) : (
              'Create Account'
            )}
          </Button>
        </Box>

        {/* Link to Login */}
        <Box
          sx={{
            mt: 3,
            pt: 3,
            borderTop: `1px solid ${colors.border}`,
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" sx={{ color: colors.textSecondary }}>
            Already have an account?{' '}
            <Link
              component="button"
              type="button"
              onClick={onNavigateToLogin}
              sx={{
                color: '#22c55e',
                textDecoration: 'none',
                fontWeight: 600,
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Sign In
            </Link>
          </Typography>
        </Box>
      </Box>

      {/* Confirm-your-email step before actually submitting -- a typo'd
          email means the verification link never arrives and the signup
          silently goes nowhere, with nothing else in this flow to catch it
          (the server-side MX check catches a bad domain, not a mistyped
          mailbox on a real one, e.g. "jon.smith@" vs "john.smith@"). */}
      <Dialog open={emailConfirmOpen} onClose={() => setEmailConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: colors.textPrimary, bgcolor: colors.surface }}>
          Confirm your email
        </DialogTitle>
        <DialogContent sx={{ bgcolor: colors.surface }}>
          <DialogContentText sx={{ color: colors.textSecondary, mb: 1.5 }}>
            We'll send your verification link to:
          </DialogContentText>
          <Box
            sx={{
              display: 'flex', alignItems: 'center', gap: 1, p: 1.5,
              bgcolor: colors.background, border: `1px solid ${colors.borderLight}`,
            }}
          >
            <EmailIcon sx={{ color: '#22c55e', fontSize: 20 }} />
            <Typography sx={{ color: colors.textPrimary, fontWeight: 600, wordBreak: 'break-all' }}>
              {email.trim()}
            </Typography>
          </Box>
          <DialogContentText sx={{ color: colors.textMuted, mt: 1.5, fontSize: '0.85rem' }}>
            Double-check this is correct -- you won't be able to log in until you click the link we send here.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ bgcolor: colors.surface, p: 2 }}>
          <Button onClick={() => setEmailConfirmOpen(false)} sx={{ color: colors.textSecondary }}>
            Edit
          </Button>
          <Button
            onClick={confirmRegistration}
            variant="contained"
            sx={{ bgcolor: '#22c55e', color: colors.background, fontWeight: 600, '&:hover': { bgcolor: '#16a34a' } }}
          >
            Looks good, create account
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RegisterPage;
